/**
 * Recharge review / log — GET /api/admin/recharge (optional status filter).
 * Reject body uses `reason` (see admin-recharge.routes.ts).
 */
import { useState } from 'react';
import { Button, Image, Input, Modal, Select, Space, Tag, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface RechargeRow {
  id: number;
  user_id: number;
  username: string;
  asset: string;
  amount: string;
  channel: string;
  tx_hash: string | null;
  proof_url: string | null;
  status: 'pending' | 'reviewing' | 'approved' | 'rejected';
  create_time: string;
  review_time: string | null;
  review_note: string | null;
}

const STATUS_TAG: Record<string, { color: string; text: string }> = {
  pending: { color: 'default', text: '待提交' },
  reviewing: { color: 'orange', text: '审核中' },
  approved: { color: 'green', text: '已通过' },
  rejected: { color: 'red', text: '已拒绝' },
};

export interface RechargeReviewPageProps {
  /** Default filter: `reviewing` for 充值审核; omit for 充值记录 (all statuses). */
  defaultStatusFilter?: string;
  title?: string;
}

export function RechargeReviewPage({
  defaultStatusFilter = 'reviewing',
  title = '充值审核',
}: RechargeReviewPageProps = {}) {
  const [status, setStatus] = useState<string | undefined>(defaultStatusFilter);
  const [rejectTarget, setRejectTarget] = useState<RechargeRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const hasNode = useAuthStore((s) => s.hasNode);
  const qc = useQueryClient();

  const canApprove = hasNode('platform.recharge.approve');
  const canReject = hasNode('platform.recharge.reject');

  async function approve(row: RechargeRow) {
    try {
      await api.post(`/api/admin/recharge/${row.id}/approve`);
      message.success(`已通过充值 #${row.id}`);
      qc.invalidateQueries({ queryKey: ['admin', 'recharge'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '审核通过失败');
    }
  }

  async function reject() {
    if (!rejectTarget) return;
    try {
      await api.post(`/api/admin/recharge/${rejectTarget.id}/reject`, { reason: rejectReason });
      message.success(`已拒绝充值 #${rejectTarget.id}`);
      setRejectTarget(null);
      setRejectReason('');
      qc.invalidateQueries({ queryKey: ['admin', 'recharge'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '拒绝失败');
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '用户', dataIndex: 'username', width: 140 },
    { title: '资产', dataIndex: 'asset', width: 90, render: (v: string) => v.toUpperCase() },
    { title: '金额', dataIndex: 'amount', width: 140 },
    { title: '渠道', dataIndex: 'channel', width: 120 },
    { title: '交易哈希', dataIndex: 'tx_hash', ellipsis: true },
    {
      title: '凭证',
      dataIndex: 'proof_url',
      width: 80,
      render: (v: string | null) =>
        v ? <Image width={40} src={v} /> : '—',
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (v: string) => {
        const t = STATUS_TAG[v] ?? { color: 'default', text: v };
        return <Tag color={t.color}>{t.text}</Tag>;
      },
    },
    { title: '提交时间', dataIndex: 'create_time', width: 170 },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_: unknown, row: RechargeRow) =>
        row.status === 'reviewing' || row.status === 'pending' ? (
          <Space>
            {canApprove && (
              <Button size="small" type="primary" onClick={() => approve(row)}>
                通过
              </Button>
            )}
            {canReject && (
              <Button size="small" danger onClick={() => setRejectTarget(row)}>
                拒绝
              </Button>
            )}
          </Space>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>{title}</h2>
      <Space style={{ marginBottom: 12 }}>
        <Select
          value={status}
          style={{ width: 160 }}
          onChange={setStatus}
          allowClear
          placeholder="状态"
          options={[
            { value: 'pending', label: '待提交' },
            { value: 'reviewing', label: '审核中' },
            { value: 'approved', label: '已通过' },
            { value: 'rejected', label: '已拒绝' },
          ]}
        />
      </Space>

      <DataTable<RechargeRow>
        queryKey={['admin', 'recharge', status ?? 'all']}
        endpoint="/api/admin/recharge"
        extraParams={status !== undefined && status !== null ? { status } : {}}
        columns={columns}
        rowKey="id"
        scroll={{ x: 1400 }}
      />

      <Modal
        open={rejectTarget !== null}
        title={`拒绝充值 #${rejectTarget?.id}`}
        onOk={reject}
        onCancel={() => {
          setRejectTarget(null);
          setRejectReason('');
        }}
        okText="确认拒绝"
        okButtonProps={{ danger: true, disabled: !rejectReason }}
      >
        <Input.TextArea
          rows={3}
          placeholder="拒绝原因（用户可见）"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>
    </div>
  );
}
